import React, { useEffect, useMemo, useContext, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useSelector } from "react-redux";
import { GET_CLIENT_NAME } from "../queries/clients";

const UserTenantsContext = React.createContext(null);

export function UserTenantsProvider({ children }) {
  const associatedTenantsIds = useSelector(
    (state) => state?.auth?.associatedTenantUsers
  );
  const tenantIds = useMemo(
    () => associatedTenantsIds || [],
    [associatedTenantsIds]
  );

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [fetchClientName] = useLazyQuery(GET_CLIENT_NAME, {
    fetchPolicy: "cache-first",
  });

  useEffect(() => {
    if (tenantIds.length === 0) {
      setTenants([]);
      return;
    }

    const fetchTenants = async () => {
      setLoading(true);
      setError(null);

      try {
        const results = await Promise.all(
          tenantIds.map(async (data) => {
            try {
              const { data: result } = await fetchClientName({
                variables: { clientId: data?.tenantId },
              });
              return result?.client;
            } catch {
              return null;
            }
          })
        );

        const seen = new Set();
        const list = [];
        for (const tenant of results) {
          if (tenant?.id != null && !seen.has(tenant.id)) {
            seen.add(tenant.id);
            list.push({ id: `${tenant.id}`, name: tenant.name });
          }
        }
        setTenants(list);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    };

    void fetchTenants();
  }, [tenantIds, fetchClientName]);

  const value = useMemo(() => {
    const tenantNamesById = tenants.reduce((acc, tenant) => {
      acc[`${tenant.id}`] = tenant.name;
      return acc;
    }, {});

    return {
      tenants,
      tenantNamesById,
      getTenantNameById: (tenantId) => tenantNamesById[`${tenantId}`],
      loading,
      error,
    };
  }, [tenants, loading, error]);

  return (
    <UserTenantsContext.Provider value={value}>
      {children}
    </UserTenantsContext.Provider>
  );
}

export function useUserTenants() {
  const ctx = useContext(UserTenantsContext);
  if (!ctx)
    throw new Error("useUserTenants must be used within a UserTenantsProvider");
  return ctx;
}
